// @ts-check

const FOXSELL_EVENTS = {
  bundleUpdated: 'foxsell:bundle-updated',
}

class FoxsellMixMatch extends HTMLElement {
  constructor() {
    super();
    this.selectedItems = new Map();
    this.config = {};

    try {
      const configElement = this.querySelector('#foxsell-config[type="application/json"]');
      this.config = configElement ? JSON.parse(configElement.textContent || '{}') : {};
    } catch (error) {
      console.error('Failed to parse foxsell config:', error);
      this.config = {};
    }
  }

  connectedCallback() {
    this.validateBundle();
    this.bindATCButtons();
  }

  bindATCButtons() {
    const submitButton = document.querySelector('product-form .PDP-product-submit');
    if (!submitButton) return;

    document.querySelectorAll('button[type="button"].custom-atc-button').forEach(btn => {
      if (btn.dataset.foxsellAtcBound === 'true') return;
      btn.dataset.foxsellAtcBound = 'true';
      btn.addEventListener('click', () => {
        const submit = document.querySelector('product-form .PDP-product-submit');
        if (submit) submit.click();
      });
    });
  }

  getCategoryConfig(categoryId) {
    return this.config.categories?.find((category) => category.id === categoryId);
  }

  getCategory(categoryId, createIfNotExists = false) {
    const categoryConfig = this.getCategoryConfig(categoryId);
    if(!categoryConfig) return null;

    const category = this.selectedItems.get(categoryId);
    if(!category && createIfNotExists) {
      this.selectedItems.set(categoryId, {
        items: new Map(),
        quantity: 0,
        id: categoryId,
        title: categoryConfig.title,
        price: this.config.settings.price,
        isMaxQuantity: false,
        maxQuantity: categoryConfig.quantity,
      });
      return this.selectedItems.get(categoryId);
    }
    return category;
  }

  getSerializedCategory(categoryId) {
    const selectedCategory = this.getCategory(categoryId);
    if(!selectedCategory) return null;

    return {
      ...selectedCategory,
      items: Array.from(selectedCategory.items.values())
    }
  }

  getItem(itemId, categoryId) {
    const selectedCategory = this.getCategory(categoryId);
    if(!selectedCategory) return null;

    return selectedCategory.items.get(itemId);
  }

  getSelectedItems() {
    // serialize the selected items
    return Array.from(this.selectedItems.values()).map(category => ({
      id: category.id,
      title: category.title,
      quantity: category.quantity,
      maxQuantity: category.maxQuantity,
      isMaxQuantity: category.isMaxQuantity,
      price: category.price,
      items: Array.from(category.items.values())
    }));
  }

  addToBundle(item, quantity, categoryId) {
    const selectedCategory = this.getCategory(categoryId, true);
    if(!selectedCategory) return;

    // clear the current category
    if(selectedCategory.items.size > 0) {
      selectedCategory.items.clear();
      selectedCategory.quantity = 0;
    }

    const qoaEnabled = this.config.options?.length > 0;
    if(((selectedCategory.quantity + quantity) > selectedCategory.maxQuantity) && !qoaEnabled) return;

    const selectedItem = this.getItem(item.id, categoryId);
    if(!selectedItem) {
      selectedCategory.items.set(item.id, {
        quantity: quantity,
        ...item
      });
    } else {
      selectedItem.quantity += quantity;
    }
    selectedCategory.quantity += quantity;

    this.validateBundle();

    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.getSelectedItems(),
        action: 'add',
        item: this.getItem(item.id, categoryId),
        category: this.getSerializedCategory(categoryId),
      }
    }));
  }

  removeFromBundle(categoryId) {
    const selectedCategory = this.getCategory(categoryId);
    if(!selectedCategory) return;

    // clear the current category
    this.selectedItems.delete(categoryId);


    // const selectedItem = this.getItem(item.id, categoryId);
    // if(!selectedItem) return;

    // const newItemQuantity = Math.max(selectedItem.quantity - quantity, 0);
    // if(newItemQuantity === 0) {
    //   selectedCategory.items.delete(item.id);
    // } else {
    //   selectedItem.quantity = newItemQuantity;
    // }

    // selectedCategory.quantity = Math.max(selectedCategory.quantity - quantity, 0);
    // if(selectedCategory.quantity <= 0) {
    //   this.selectedItems.delete(categoryId);
    // }

    this.validateBundle();

    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: this.getSelectedItems(),
        action: 'remove',
        // item: {...selectedItem, quantity: 0},
        category: {
          ...selectedCategory,
          isMaxQuantity: this.getCategory(categoryId)?.isMaxQuantity || false,
          items: this.getSerializedCategory(categoryId)?.items || []
        }
      }
    }));
  }

  clearBundle() {
    this.selectedItems.clear();
    this.validateBundle();
    this.dispatchEvent(new CustomEvent(FOXSELL_EVENTS.bundleUpdated, {
      detail: {
        bundle: [],
        action: 'clear'
      }
    }));
  }

  validateBundle() {
    if(!this.config) return;
    const qoaEnabled = this.config.options?.length > 0;
    qoaEnabled ? this.validateBundleWithQOA() : this.validateBundleWithoutQOA();
  }

  validateBundleWithQOA() {
    if(!this.config || !this.config.options) {
      this.toggleAddToCartButton(true);
      return;
    }

    // Allow users to add more items than the max quantity
    const allowOverflow = false;

    const optionLimits = (this.config.options || [])
      .map(opt => Number(opt.quantity ?? opt))
      .filter(n => !Number.isNaN(n));

    const maxQuantity = optionLimits.length ? Math.max(...optionLimits) : 0;
    const minQuantity = optionLimits.length ? Math.min(...optionLimits) : 0;

    const items = this.getSelectedItems().flatMap(category =>
      Array.isArray(category.items) ? category.items : []
    );

    const itemsCount = items.reduce((sum, item) => sum + item.quantity, 0);

    const currentValidOption = this.config.options.findLast(opt => Number(opt.quantity ?? opt) <= itemsCount);
    // this.config.settings.price = currentValidOption?.price ?? null;

    let isValid = false;

    if(allowOverflow) {
      isValid = itemsCount >= minQuantity;
    } else {
      isValid = Array.isArray(this.config.options) 
      && this.config.options.some(opt => Number(opt.quantity ?? opt) === itemsCount);
    }

    this.selectedItems.forEach(category => {
      category.isMaxQuantity = allowOverflow ? !allowOverflow : (itemsCount >= maxQuantity);
    });

    this.updateLineItemProperties();
    this.toggleAddToCartButton(!isValid);
  }

  validateBundleWithoutQOA() {
    if(!this.config || !this.config.categories) {
      this.toggleAddToCartButton(true);
      return;
    }

    const isValid = this.config.categories.reduce((allValid, category) => {
      const selectedCategory = this.getCategory(category.id);
      if (!selectedCategory) return false;
      const atMax = selectedCategory.quantity >= selectedCategory.maxQuantity;
      selectedCategory.isMaxQuantity = atMax;
      return allValid && atMax;
    }, true);

    this.updateLineItemProperties();
    this.toggleAddToCartButton(!isValid);
  }

  updateLineItemProperties() {
    const bundleIdInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_id]"]');
    const itemInput = this.querySelector('input[name="properties[__foxsell:dynamic_add_on_bundle_items]"]');
    const extraPropertiesInput = this.querySelector('input[name="properties[__foxsell:bundle_info]"]');
    const itemsCount = this.getSelectedItems().reduce((sum, category) => sum + category.quantity, 0);
    
    if (!itemInput || !bundleIdInput || !this.config.bundleId || !extraPropertiesInput) return;

    // add a timestamp to the bundle id to get unique bundle ids
    const bundleId = this.config.bundleId + '_' + Date.now();
    bundleIdInput.setAttribute('value', bundleId);

    if (this.config.options?.length > 0) {
      const currentValidOption = this.config.options.findLast(opt => Number(opt.quantity ?? opt) <= itemsCount);
      const formId = bundleIdInput.getAttribute('form');
      const form = document.querySelector(`form[id="${formId}"]`);
      if (form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.setAttribute('value', currentValidOption?.variant_id ?? '');
      }
    }

    const lineItems = this.getSelectedItems().flatMap(category =>
      (category.items || []).map(item => ({
        variantId: Number(item.id) || item.id,
        quantity: item.quantity || 1,
        category: category.title,
        type: 'product',
        properties: item.properties || {}
      }))
    );

    itemInput.setAttribute('value', JSON.stringify(lineItems));

    const extraProperties = this.getSelectedItems().flatMap(category =>
      (category.items || []).map(item => ({
        product_title: item.product.title,
        variant_title: item.title,
        is_default_variant: item.product.has_only_default_variant,
        image: item.featured_image?.src || item.product.featured_image?.src
      }))
    );

    extraPropertiesInput.setAttribute('value', JSON.stringify(extraProperties));

    // Gallery keys, matched against each media's alt (case-insensitive) or media id:
    //  - "*"                       always-on hero
    //  - joined colors             legacy fixed bundles tag media "Black / Black"
    //  - product title, "title / color"  manually tagged images
    //  - the selected products' own media ids (foxsell-mix-match.liquid emits handle -> ids), since
    //    bundle galleries reuse the component products' media files.
    const selected = this.getSelectedItems().flatMap(category => category.items || []);
    const keys = ['*', selected.map(item => item.option1).join(' / ')];
    const mediaByHandle = this.bundleMedia || (this.bundleMedia = JSON.parse(
      document.getElementById(`foxsell-bundle-media-${this.dataset.sectionId}`)?.textContent || '{}'
    ));
    selected.forEach(item => {
      keys.push(item.product.title, item.product.title + ' / ' + item.option1);
      (mediaByHandle[item.product.handle] || []).forEach(id => keys.push(String(id)));
    });

    this.updateProductMedia(keys);
  }

  toggleAddToCartButton(disable) {
    const addToCartButtons = document.querySelectorAll('button[type="button"].custom-atc-button');
    if(!addToCartButtons.length) return;

    const primaryButton = addToCartButtons[0];
    if(!this.initialAddToCartButtonHTML) {
      this.initialAddToCartButtonHTML = primaryButton.innerHTML;
    }

    const items = this.getSelectedItems().flatMap((category) =>
      (Array.isArray(category.items) ? category.items : []).map(item => ({
        ...item,
        category: {
          id: category.id,
          title: category.title,
          quantity: category.quantity,
          maxQuantity: category.maxQuantity
        }
      }))
    );

    const totalPrice = items.reduce((sum, item) => sum + item.foxsell_price * item.quantity, 0);
    let discountedTotalPrice = 0;
    if(this.config.settings.price.strategy === 'dynamic_pricing') {
      const discount = this.config.discountPercentage;
      discountedTotalPrice = totalPrice - (totalPrice * (discount / 100));
    };
    const newHTML = totalPrice > 0 && discountedTotalPrice > 0
      ? `${this.initialAddToCartButtonHTML}
      <span class="foxsell-total-price">
        <span class="foxsell-total-price-label">-</span>
        ${discountedTotalPrice < totalPrice ? `
          <span class="foxsell-slashed-price">${window.foxsell.formatMoney(totalPrice)}</span> 
          <span class="foxsell-discounted-price">${window.foxsell.formatMoney(discountedTotalPrice)}</span>
        ` : `
          <span class="price-item price-item--regular">${window.foxsell.formatMoney(totalPrice)}</span>
        `}
      </span>
      `
      : this.initialAddToCartButtonHTML;

    addToCartButtons.forEach((btn) => {
      btn.innerHTML = newHTML;
      btn.toggleAttribute('disabled', disable);
    });

    const priceEl = document.querySelector('.price__container');
    if(!priceEl) return;

    if(!this.initialPriceHTML) {
      this.initialPriceHTML = priceEl.innerHTML;
    }

    if(totalPrice > 0 && discountedTotalPrice > 0) {
      priceEl.innerHTML = `
       ${discountedTotalPrice < totalPrice ? `
        <span class="foxsell-slashed-price">${window.foxsell.formatMoney(totalPrice)}</span> 
        <span class="foxsell-discounted-price">${window.foxsell.formatMoney(discountedTotalPrice)}</span>
        ` : `
        <span class="foxsell-total-price">${window.foxsell.formatMoney(totalPrice)}</span>
        `}
      `;
    } else {
      priceEl.innerHTML = this.initialPriceHTML;
    }

    const priceElement = document.querySelector('.price-item.price-item--regular');
    if(priceElement) {
      priceElement.innerHTML = window.foxsell.formatMoney(totalPrice);
    }
  }


  updateProductMedia(keys) {
    const wanted = keys.map((k) => String(k).trim().toLowerCase());
    const matches = (el, altAttr) =>
      wanted.includes((el.getAttribute(altAttr) || '').trim().toLowerCase()) ||
      wanted.includes(el.getAttribute('data-media-id') || '');

    const gridGallery = document.querySelector('[id^="GridGallery-"]');
    if (gridGallery) {
      gridGallery.dispatchEvent(new CustomEvent('product-grid-gallery:select', {
        detail: { keys }
      }));
      // The grid is desktop-only; below 990px the swiper gallery is the visible one.
      if (window.innerWidth >= 990) return;
    }

    const mainWrapper = document.querySelector('.slider__images .swiper-wrapper');
    const thumbWrapper =
      window.innerWidth < 990
        ? document.querySelector('.slider__thumbs_custom .swiper-wrapper-main')
        : document.querySelector('.slider__thumbs .swiper-wrapper');
  
    if (!mainWrapper || !thumbWrapper) return;
  
    // Clone from original slide arrays
    const allMainSlides = window._originalMainSlides.map((s) => s.cloneNode(true));
    const allThumbSlides = (
      window.innerWidth < 990 ? window._originalMobileThumbSlides : window._originalDesktopThumbSlides
    ).map((s) => s.cloneNode(true));
  
    // Destroy Swipers
    if (window.sliderImages?.destroy) window.sliderImages.destroy(true, true);
    if (window.sliderThumbs?.destroy) window.sliderThumbs.destroy(true, true);
  
    // Clear wrappers
    mainWrapper.innerHTML = '';
    thumbWrapper.innerHTML = '';
  
    // Rebuild main slides
    let addedMain = 0;
    allMainSlides.forEach((slide) => {
      if (matches(slide, 'data-media-color')) {
        mainWrapper.appendChild(slide);
        addedMain++;
      }
    });
  
    // Rebuild thumbs
    let thumbIndex = 0;
    let firstThumb = null;
    allThumbSlides.forEach((thumb) => {
      if (matches(thumb, 'data-media-color-thumb')) {
        thumb.dataset.index = thumbIndex;
        thumbWrapper.appendChild(thumb);
        if (thumbIndex === 0) firstThumb = thumb;
        thumbIndex++;
      }
    });
  
    // Fallback: restore all if none matched
    if (addedMain === 0) {
      allMainSlides.forEach((s) => mainWrapper.appendChild(s.cloneNode(true)));
      allThumbSlides.forEach((t, i) => {
        const clone = t.cloneNode(true);
        clone.dataset.index = i;
        thumbWrapper.appendChild(clone);
      });
    }
  
    setTimeout(() => {
      // Init thumbs swiper
      const thumbSelector =
        window.innerWidth < 990 ? '.slider__thumbs_custom .swiper-container' : '.slider__thumbs .swiper-container';
  
      window.sliderThumbs = new Swiper(thumbSelector, {
        direction: window.innerWidth >= 990 ? 'vertical' : 'horizontal',
        slidesPerView: window.innerWidth >= 990 ? 8 : 5.6,
        spaceBetween: window.innerWidth >= 990 ? 4 : 2,
      });
  
      // Init main swiper
      window.sliderImages = new Swiper('.slider__images .swiper-container', {
        slidesPerView: window.innerWidth >= 750 ? 1 : 1.12,
        spaceBetween: window.innerWidth >= 750 ? 12 : 2,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        thumbs: {
          swiper: window.sliderThumbs,
        },
        on: {
          slideChange: function () {
            document.querySelectorAll('.slider__images video').forEach((v) => v.pause());
  
            const current = this.slides[this.activeIndex];
            const mediaId = current.getAttribute('data-media-id');
  
            document
              .querySelectorAll('.swiper-slide-thumb-active')
              .forEach((t) => t.classList.remove('swiper-slide-thumb-active'));
  
            document
              .querySelectorAll(
                `.slider__thumbs .swiper-slide[data-media-id="${mediaId}"],
                    .slider__thumbs_custom .swiper-slide-main[data-media-id="${mediaId}"]`
              )
              .forEach((t) => {
                t.classList.add('swiper-slide-thumb-active');
              });
          },
        },
      });
  
      // Click on thumbnails
      const rebuiltThumbs = thumbWrapper.querySelectorAll('.swiper-slide, .swiper-slide-main');
      rebuiltThumbs.forEach((thumb) => {
        thumb.addEventListener('click', function () {
          const index = parseInt(this.dataset.index, 10);
          if (!isNaN(index)) {
            window.sliderImages.slideTo(index);
          }
  
          rebuiltThumbs.forEach((t) => t.classList.remove('swiper-slide-thumb-active'));
          this.classList.add('swiper-slide-thumb-active');
        });
      });
  
      // Initial active class and slide
      if (firstThumb) {
        firstThumb.classList.add('swiper-slide-thumb-active');
        window.sliderImages.slideTo(0);
      }
  
      window.sliderThumbs.update();
      window.sliderImages.update();
    }, 50);
  }
}

customElements.define('foxsell-mix-match', FoxsellMixMatch);


// Foxsell Product Card Carousel
class FoxsellProductCardCarousel extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.slides = this.querySelectorAll('foxsell-product-card');
    if (this.slides.length <= 1) {
      this.dataset.active = 'false'
      return;
    };
    
    this.dataset.active = 'true';
    this.setupCarousel();
  }

  setupCarousel() {
    if(!this.slides || this.slides.length == 0) return;

    this.activeSlideIndex = 0;
    this.slides[0].classList.add('slide--active');
    this.slides[0].querySelector('.foxsell-product-card__arrow--prev').disabled = true;
    this.slides[this.slides.length - 1].querySelector('.foxsell-product-card__arrow--next').disabled = true;

    const prevButtons = this.querySelectorAll('.foxsell-product-card__arrow--prev');
    const nextButtons = this.querySelectorAll('.foxsell-product-card__arrow--next');

    prevButtons.forEach(button => {
      button.addEventListener('click', this.prevSlide.bind(this));
    });

    nextButtons.forEach(button => {
      button.addEventListener('click', this.nextSlide.bind(this));
    });
  }

  nextSlide() {
    this.slides[this.activeSlideIndex].classList.remove('slide--active');
    this.activeSlideIndex++;
    this.slides[this.activeSlideIndex].classList.add('slide--active');
    this.updateCurrentSlide();
  }

  prevSlide() {
    this.slides[this.activeSlideIndex].classList.remove('slide--active');
    this.activeSlideIndex--;
    this.slides[this.activeSlideIndex].classList.add('slide--active');
    this.updateCurrentSlide();
  }

  updateCurrentSlide() {
    const productCard = this.slides[this.activeSlideIndex];
    if(!productCard) return;

    const categoryCheckbox = this.querySelector('.foxsell-category__product-grid__checkbox input[type="checkbox"]');

    if(categoryCheckbox?.checked) {
      productCard.addToBundle();
    }
  }
}
customElements.define('foxsell-product-card-carousel', FoxsellProductCardCarousel);
